import { useEffect, useState } from "react";
import { KeyRound, Trash2, Copy, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Panel } from "@/components/Panel";
import { http, useResource, formatError, API } from "@/lib/api";

const MODES = [
  { v: "all", l: "Ask before every change" },
  { v: "risky", l: "Do it right away; confirm only deletions, payments & approvals" },
  { v: "none", l: "Do everything right away" },
];

export const OmvexisModePanel = () => {
  const [mode, setMode] = useState(null);
  const [auto, setAuto] = useState(true);
  const [remind, setRemind] = useState(true);
  useEffect(() => {
    http.get("/omvexis/status").then((r) => setMode(r.data.mode));
    http.get("/settings").then((r) => { setAuto(r.data.omvexis_autopilot !== false); setRemind(r.data.buyer_reminders !== false); });
  }, []);
  const toggleRemind = async () => {
    const v = !remind;
    setRemind(v);
    try { await http.put("/settings", { buyer_reminders: v }); toast.success(v ? "Buyer reminders on" : "Buyer reminders off"); } catch (e) { toast.error(formatError(e)); }
  };
  const toggleAuto = async () => {
    const v = !auto;
    setAuto(v);
    try { await http.put("/settings", { omvexis_autopilot: v }); toast.success(v ? "Overdue autopilot on" : "Overdue autopilot off"); } catch (e) { toast.error(formatError(e)); }
  };
  const save = async (v) => {
    setMode(v);
    try { await http.put("/settings", { omvexis_mode: v }); toast.success("Omvexis safety mode saved"); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <Panel label="Omvexis Link" title="When JARVIS changes Omvexis" testId="settings-omvexis-mode">
      <div className="space-y-2">
        {MODES.map((m) => (
          <label key={m.v} className={`flex items-center gap-3 border px-3 py-2 cursor-pointer transition-colors ${mode === m.v ? "border-cyan-400 bg-cyan-950/40" : "border-cyan-500/20 hover:border-cyan-500/50"}`} data-testid={`omvexis-mode-${m.v}`}>
            <input type="radio" name="omx-mode" checked={mode === m.v} onChange={() => save(m.v)} className="accent-cyan-400" />
            <span className="text-sm text-slate-200">{m.l}</span>
          </label>
        ))}
      </div>
      <label className="mt-5 flex items-start gap-3 cursor-pointer" data-testid="omvexis-autopilot-toggle">
        <input type="checkbox" checked={auto} onChange={toggleAuto} className="accent-cyan-400 mt-1" />
        <span className="text-sm text-slate-200">Overdue autopilot — every morning at 08:00, queue payment-due notices for all overdue Omvexis shipments for one-tap confirm</span>
      </label>
      <label className="mt-3 flex items-start gap-3 cursor-pointer" data-testid="buyer-reminders-toggle">
        <input type="checkbox" checked={remind} onChange={toggleRemind} className="accent-cyan-400 mt-1" />
        <span className="text-sm text-slate-200">Overdue escalation — every morning email each overdue buyer a reminder (polite, then firmer at 7, 15 and 30 days) and send the team the overdue list</span>
      </label>
    </Panel>
  );
};

const snippet = () => `<script src="${process.env.REACT_APP_BACKEND_URL}/api/public/jarvis-widget.js" defer></script>`;

export const WebsiteBubblePanel = () => {
  const [kb, setKb] = useState(null);
  useEffect(() => { http.get("/settings").then((r) => setKb(r.data.website_kb || "")); }, []);
  const save = async () => {
    try { await http.put("/settings", { website_kb: kb }); toast.success("Website knowledge saved"); } catch (e) { toast.error(formatError(e)); }
  };
  const copy = () => { navigator.clipboard.writeText(snippet()); toast.success("Snippet copied"); };
  return (
    <Panel label="omvexis.com" title="Website Jarvis Bubble" testId="settings-website-bubble" className="xl:col-span-2">
      <p className="text-slate-400 text-sm mb-4">Paste this one line before &lt;/body&gt; on omvexis.com (replacing the current "Ask Jarvis" bubble). Buyers get a live product assistant that answers from the knowledge below and turns quote requests into CRM leads + an email to your team. It has no access to Omvexis portal data.</p>
      <div className="flex gap-2 items-start mb-5">
        <code className="font-mono-hud text-[12px] text-cyan-100 break-all bg-black/30 border border-white/10 rounded-2xl p-3 flex-1" data-testid="widget-snippet">{snippet()}</code>
        <button className="hud-btn hud-btn-ghost shrink-0" onClick={copy} data-testid="widget-snippet-copy"><Copy size={13} /> Copy</button>
        <a className="hud-btn shrink-0" href="/widget" target="_blank" rel="noreferrer" data-testid="widget-preview-link">Preview</a>
      </div>
      <div className="hud-label mb-2">What Jarvis knows (products, specs, trade terms)</div>
      {kb !== null && <textarea className="hud-input min-h-[220px] text-[13px]" value={kb} onChange={(e) => setKb(e.target.value)} data-testid="widget-kb-input" />}
      <button className="hud-btn mt-3" onClick={save} data-testid="widget-kb-save">Save knowledge</button>
    </Panel>
  );
};

export const PendingBanner = () => {
  const [items, setItems] = useState([]);
  const load = () => http.get("/omvexis/pending").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); window.addEventListener("jarvis:refresh", load); return () => window.removeEventListener("jarvis:refresh", load); }, []);
  if (!items.length) return null;
  const all = async (kind) => {
    for (const it of items) await http.post(`/omvexis/pending/${it.id}/${kind}`).catch(() => {});
    toast.success(kind === "confirm" ? `${items.length} change(s) sent to Omvexis` : "Cancelled");
    load();
  };
  return (
    <div className="hud-panel !rounded-full px-6 py-3 mb-6 flex flex-wrap items-center justify-between gap-3 fade-up" data-testid="pending-banner">
      <span className="text-sm text-slate-200"><span className="text-cyan-300 font-semibold" data-testid="pending-banner-count">{items.length}</span> Omvexis change(s) awaiting your confirmation — say "confirm all" or tap below.</span>
      <div className="flex gap-2">
        <a href="/omvexis" className="hud-btn hud-btn-ghost !py-1.5" data-testid="pending-banner-review">Review</a>
        <button className="hud-btn !py-1.5" onClick={() => all("confirm")} data-testid="pending-banner-confirm-all">Confirm all</button>
        <button className="hud-btn hud-btn-ghost !py-1.5" onClick={() => all("cancel")} data-testid="pending-banner-cancel-all">Cancel all</button>
      </div>
    </div>
  );
};

export const ApiKeysPanel = () => {
  const [keys, reload] = useResource("/keys");
  const [name, setName] = useState("");
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try { const { data } = await http.post("/keys", { name: name || "Omvexis" }); setFresh(data.key); setName(""); reload(); }
    catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };
  const curl = `curl -X POST ${API}/v1/command -H "X-API-Key: YOUR_KEY" -H "Content-Type: application/json" -d '{"command":"List overdue Omvexis shipments"}'`;
  return (
    <Panel label="Developer Access" title="JARVIS API Keys" testId="settings-api-keys" className="xl:col-span-2">
      <p className="text-slate-400 text-sm mb-4">Let Omvexis or any software send JARVIS commands. Responses return JARVIS's reply, executed actions and any changes awaiting your confirmation.</p>
      <div className="flex gap-2 mb-4 max-w-xl">
        <input className="hud-input" placeholder="Key name (e.g. Omvexis server)" value={name} onChange={(e) => setName(e.target.value)} data-testid="api-key-name-input" />
        <button className="hud-btn shrink-0" onClick={create} disabled={busy} data-testid="api-key-create-button">{busy ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />} Create key</button>
      </div>
      {fresh && (
        <div className="border border-emerald-500/40 bg-emerald-950/20 p-3 mb-4" data-testid="api-key-fresh">
          <div className="hud-label !text-emerald-300 mb-1 flex items-center gap-1"><ShieldCheck size={12} /> Copy now — shown only once</div>
          <div className="flex gap-2 items-center"><code className="font-mono-hud text-[12px] text-emerald-100 break-all" data-testid="api-key-fresh-value">{fresh}</code>
            <button className="hud-btn hud-btn-ghost !py-1 shrink-0" onClick={() => copy(fresh)} data-testid="api-key-copy"><Copy size={12} /></button></div>
        </div>
      )}
      <table className="w-full text-left mb-4"><tbody>
        {(keys || []).map((k) => (
          <tr key={k.id} className="border-b border-cyan-900/50" data-testid={`api-key-row-${k.id}`}>
            <td className="py-2 px-3 text-slate-100">{k.name}</td>
            <td className="px-3 font-mono-hud text-[12px] text-slate-400">{k.prefix}…</td>
            <td className="px-3 font-mono-hud text-[11px] text-slate-500">{k.last_used ? `used ${new Date(k.last_used).toLocaleString()}` : "never used"}</td>
            <td className="px-3 text-right"><button className="text-slate-500 hover:text-red-300" onClick={() => http.delete(`/keys/${k.id}`).then(() => { setFresh(null); reload(); })} data-testid={`api-key-revoke-${k.id}`}><Trash2 size={14} /></button></td>
          </tr>
        ))}
      </tbody></table>
      <div className="hud-label mb-1">Example</div>
      <div className="flex gap-2 items-start"><code className="font-mono-hud text-[11px] text-cyan-100/80 break-all bg-slate-950/60 border border-cyan-500/20 p-2 flex-1" data-testid="api-example">{curl}</code>
        <button className="hud-btn hud-btn-ghost !py-1 shrink-0" onClick={() => copy(curl)}><Copy size={12} /></button></div>
    </Panel>
  );
};
